/**
 * StatsPage - Main stats dashboard page with hierarchical filtering.
 */

import { useState } from 'react'
import { subDays } from 'date-fns'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { StatsFilterBar } from '@/features/stats/components/StatsFilterBar'
import { OverviewDashboard } from '@/features/stats/components/OverviewDashboard'
import { WeeklyTrendsView } from '@/features/stats/components/WeeklyTrendsView'
import { DomainAnalysisView } from '@/features/stats/components/DomainAnalysisView'
import type { Scope } from '@/features/stats/types'

export function StatsPage() {
  // Hierarchical filter state
  const [scope, setScope] = useState<Scope>('system')
  const [selectedTenant, setSelectedTenant] = useState<string | null>(null)
  const [selectedOrg, setSelectedOrg] = useState<string | null>(null)

  // Date range (last 30 days by default)
  const [dateRange] = useState({
    start: subDays(new Date(), 30),
    end: new Date()
  })

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Stats Dashboard</h1>
        <p className="text-muted-foreground">
          Business intelligence and insights with hierarchical filtering
        </p>
      </div>

      {/* Hierarchical Filter Bar */}
      <StatsFilterBar
        scope={scope}
        onScopeChange={setScope}
        selectedTenant={selectedTenant}
        onTenantChange={setSelectedTenant}
        selectedOrg={selectedOrg}
        onOrgChange={setSelectedOrg}
      />

      {/* View Tabs */}
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="trends">Weekly Trends</TabsTrigger>
          <TabsTrigger value="domains">Domain Analysis</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <OverviewDashboard
            scope={scope}
            tenantId={selectedTenant}
            organizationId={selectedOrg}
            start={dateRange.start.toISOString()}
            end={dateRange.end.toISOString()}
          />
        </TabsContent>

        <TabsContent value="trends" className="mt-6">
          <WeeklyTrendsView
            scope={scope}
            tenantId={selectedTenant}
            organizationId={selectedOrg}
          />
        </TabsContent>

        <TabsContent value="domains" className="mt-6">
          <DomainAnalysisView
            scope={scope}
            tenantId={selectedTenant}
            organizationId={selectedOrg}
            start={dateRange.start.toISOString()}
            end={dateRange.end.toISOString()}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}
